import React from "react";
import { ArrowRight, Leaf } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-40 md:pt-48 pb-16 md:pb-24 overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-mono-ui uppercase tracking-widest mb-8">
            <Leaf className="w-3.5 h-3.5 text-accent" />
            Farm-direct · Batch 026
          </div>

          <h1
            data-testid="hero-heading"
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[92px] font-semibold leading-[0.95] tracking-tight"
          >
            Oil. Ghee.<br />
            Vegetables the way{" "}
            <em className="italic text-accent font-medium">grandmother</em> bought them.
          </h1>

          <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Small-batch, cold-pressed oils. Hand-churned bilona ghee. Vegetables
            harvested the morning we ship them. No shortcuts, no shelf-life
            games — just food that tastes like it used to.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#shop"
              data-testid="hero-shop-cta"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all text-sm font-semibold"
            >
              Shop the pantry
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#reservations"
              data-testid="hero-reserve-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all text-sm font-semibold"
            >
              Reserve bulk order
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "26", v: "small farms" },
              { k: "48h", v: "farm → door" },
              { k: "0", v: "preservatives" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-4xl md:text-5xl font-semibold text-primary">
                  {s.k}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl hover-lift">
            <img
              src="https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=1000&auto=format&fit=crop"
              alt="Golden oil pouring"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
              <div className="font-mono-ui text-[10px] uppercase tracking-widest opacity-80">
                This week's harvest
              </div>
              <div className="font-display text-2xl font-semibold mt-1">
                Wood-pressed mustard oil
              </div>
            </div>
          </div>
          <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-accent flex items-center justify-center rotate-[-12deg] shadow-xl hidden md:flex">
            <div className="text-center">
              <div className="font-display text-2xl font-bold text-accent-foreground leading-none">
                new
              </div>
              <div className="font-mono-ui text-[10px] uppercase mt-1 text-accent-foreground">
                batch
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 md:mt-24 border-y border-border py-5 overflow-hidden">
        <div className="marquee whitespace-nowrap font-display italic text-3xl md:text-5xl text-primary/40">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 pr-16">
              <span>cold-pressed</span>
              <span className="text-accent">✦</span>
              <span>hand-churned</span>
              <span className="text-accent">✦</span>
              <span>farm-direct</span>
              <span className="text-accent">✦</span>
              <span>slow-food</span>
              <span className="text-accent">✦</span>
              <span>small-batch</span>
              <span className="text-accent">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
